import { useEffect, useRef, type PointerEvent } from 'react';
import { Size, SIGNATURE_IMAGE_PREFIX, t } from '@bms2/shared';
import { Button } from '../../components/controls';
import styles from './SignaturePad.module.css';

/**
 * 🔒 The signature pad (piece 29, the phone completion flow; decision 2061): a
 * full-width surface drawn with a finger or a mouse, answering the strokes as a
 * PNG data address, the one shape a drawn signature travels in, or null once
 * cleared. 🔨 It draws in device pixels, so a signature drawn on a phone is not
 * blurred on the frozen sheet.
 */
export function SignaturePad({ label, onChange, name }: { label: string; onChange: (image: string | null) => void; name: string }) {
  const canvas = useRef<HTMLCanvasElement | null>(null);
  const drawing = useRef(false);
  const drawn = useRef(false);

  /**
   * Sizes the drawing surface to the pad as drawn, in device pixels. 🔨 Asked
   * again at the first stroke, because a pad inside an opening window measures
   * nothing at mount, and a surface one pixel wide exports a blank signature
   * (found while building, 2026-10-05). It never runs once a stroke is down,
   * because resizing a canvas clears it.
   */
  const fit = (surface: HTMLCanvasElement) => {
    const ratio = typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1;
    const width = Math.max(1, Math.round(surface.clientWidth * ratio));
    const height = Math.max(1, Math.round(surface.clientHeight * ratio));
    if (surface.width === width && surface.height === height) return;
    surface.width = width;
    surface.height = height;
    const pen = surface.getContext?.('2d');
    if (!pen) return;
    pen.scale(ratio, ratio);
    pen.lineWidth = 2;
    pen.lineCap = 'round';
    pen.lineJoin = 'round';
    pen.strokeStyle = getComputedStyle(surface).color || 'black';
  };

  useEffect(() => {
    if (canvas.current) fit(canvas.current);
  }, []);

  const at = (event: PointerEvent<HTMLCanvasElement>) => {
    const box = event.currentTarget.getBoundingClientRect();
    return { x: event.clientX - box.left, y: event.clientY - box.top };
  };
  const down = (event: PointerEvent<HTMLCanvasElement>) => {
    if (!drawn.current) fit(event.currentTarget);
    const pen = event.currentTarget.getContext?.('2d');
    if (!pen) return;
    event.currentTarget.setPointerCapture?.(event.pointerId);
    drawing.current = true;
    const point = at(event);
    pen.beginPath();
    pen.moveTo(point.x, point.y);
  };
  const move = (event: PointerEvent<HTMLCanvasElement>) => {
    if (!drawing.current) return;
    const pen = event.currentTarget.getContext?.('2d');
    if (!pen) return;
    const point = at(event);
    pen.lineTo(point.x, point.y);
    pen.stroke();
    drawn.current = true;
  };
  const up = (event: PointerEvent<HTMLCanvasElement>) => {
    if (!drawing.current) return;
    drawing.current = false;
    if (!drawn.current) return;
    const image = event.currentTarget.toDataURL('image/png');
    onChange(image.startsWith(SIGNATURE_IMAGE_PREFIX) ? image : null);
  };
  const clear = () => {
    const surface = canvas.current;
    surface?.getContext?.('2d')?.clearRect(0, 0, surface.width, surface.height);
    drawn.current = false;
    onChange(null);
  };

  return (
    <div className={styles.pad} data-signature-pad={name}>
      <canvas ref={canvas} className={styles.surface} aria-label={label} role="img" onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerLeave={up} />
      <div className={styles.foot}>
        <span>{label}</span>
        <Button size={Size.Small} text={[t('workOrder.visitPage.clearPad')]} actions={[clear]} />
      </div>
    </div>
  );
}
